<template>
    <v-data-table
        v-bind="{
            items,
            headers,
            loading,
            itemsPerPage: -1,
            itemKey: 'tempId',
        }"
        hide-default-footer
        disable-filtering
        disable-pagination
        disable-sort
        show-expand
    >
        <template v-for="(header, index) in headers.filter((header) => header.hasOwnProperty('formatter') && header.value !== 'data-table-expand')" #[`item.${header.value}`]="{ value, item }"> <span :key="index" :title="header.withTitle ? header.formatter.bind(item)(value, item) : undefined" v-html="header.formatter.bind(item)(value, item)" /> </template>

        <template #[`item._member`]="{ item }">
            <autocomplete-member
                v-model="item._member"
                v-bind="{
                    ...attrs_input__table,
                    readonly: tempIdOnEdit !== item.tempId,
                    disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                }"
                @click="(event) => onClickField(event, item)"
                @keydown.esc="edit()"
            />
        </template>

        <template #[`item.code`]="{ item }">
            <v-text-field
                v-model="item.code"
                v-bind="{
                    ...attrs_input__table,
                    readonly: tempIdOnEdit !== item.tempId,
                    disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                }"
                @click="(event) => onClickField(event, item)"
                @keydown.esc="edit()"
            />
        </template>

        <template #[`item.type`]="{ item }">
            <v-select
                v-model="item.type"
                v-bind="{
                    ...attrs_input__table,
                    items: items_type,
                    readonly: tempIdOnEdit !== item.tempId,
                    disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                }"
                @click="(event) => onClickField(event, item)"
                @keydown.esc="edit()"
            />
        </template>

        <template #[`item.reserveRate`]="{ item }">
            <template v-if="item.type === 'RESERVE'">
                <v-currency-field
                    v-model="item.reserveRate"
                    v-bind="{
                        ...attrs_input__table,
                        min: 0,
                        max: 100,
                        allowNegative: false,
                        decimalLength: 2,
                        readonly: tempIdOnEdit !== item.tempId,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    class="text-right"
                    suffix="%"
                    @click="(event) => onClickField(event, item)"
                    @keydown.esc="edit()"
                />
            </template>
            <template v-else>
                <div class="px-3">-</div>
            </template>
        </template>

        <template #[`item.chargePrice`]="{ item }">
            <v-currency-field
                v-model="item.chargePrice"
                v-bind="{
                    ...attrs_input__table,
                    ...item.chargePrice_attrs,
                    allowNegative: false,
                    readonly: tempIdOnEdit !== item.tempId,
                    disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                }"
                class="text-right"
                suffix="원"
                @click="(event) => onClickField(event, item)"
                @keydown.esc="edit()"
            />
        </template>

        <template #[`item.chargePrice_attrs.min`]="{ item, value, header }">
            <template v-if="item.depth === 1">
                <v-currency-field
                    v-model="item.chargePrice_cost"
                    v-bind="{
                        ...attrs_input__table,
                        ...item.chargePrice_attrs,
                        min: 0,
                        allowNegative: false,
                        readonly: tempIdOnEdit !== item.tempId,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    class="text-right"
                    suffix="원"
                    @click="(event) => onClickField(event, item)"
                    @keydown.esc="edit()"
                />
            </template>
            <template v-else>
                <div class="px-3">{{ header.formatter.bind(item)(value, item) }}</div>
            </template>
        </template>

        <template #[`item.salePercent`]="{ item }">
            <v-currency-field
                v-model="item.salePercent"
                v-bind="{
                    ...attrs_input__table,
                    ...item.salePercent_attrs,
                    allowNegative: false,
                    decimalLength: 2,
                    readonly: tempIdOnEdit !== item.tempId,
                    disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                }"
                class="text-right"
                suffix="%"
                @click="(event) => onClickField(event, item)"
                @keydown.esc="edit()"
            />
        </template>

        <template #[`item.salePercent_attrs.min`]="{ item, value, header }">
            <template v-if="item.depth === 1">
                <v-currency-field
                    v-model="item.salePercent_cost"
                    v-bind="{
                        ...attrs_input__table,
                        ...item.salePercent_attrs,
                        allowNegative: false,
                        decimalLength: 2,
                        readonly: tempIdOnEdit !== item.tempId,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    class="text-right"
                    suffix="%"
                    @click="(event) => onClickField(event, item)"
                    @keydown.esc="edit()"
                />
            </template>
            <template v-else>
                <div class="px-3">{{ header.formatter.bind(item)(value, item) }}</div>
            </template>
        </template>

        <template #[`item.save`]="{ item }">
            <template v-if="tempIdOnEdit === item.tempId">
                <v-btn
                    v-bind="{
                        ...attrs_btn__icon,
                        loading,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    color="primary"
                    @click="save(item)"
                >
                    <v-icon>mdi-content-save</v-icon>
                </v-btn>
            </template>
            <template v-else>
                <v-btn
                    v-bind="{
                        ...attrs_btn__icon,
                        loading,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    color="primary"
                    @click="edit(item)"
                >
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
            </template>
        </template>

        <template #[`item.pull`]="{ item }">
            <template v-if="tempIdOnEdit === item.tempId">
                <v-btn
                    v-bind="{
                        ...attrs_btn__icon,
                        loading,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    color="error"
                    @click="edit()"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
            <template v-else>
                <v-btn
                    v-bind="{
                        ...attrs_btn__icon,
                        loading,
                        disabled: !!tempIdOnEdit && tempIdOnEdit !== item.tempId,
                    }"
                    color="error"
                    @click="pull(item)"
                >
                    <v-icon v-if="item._id">mdi-delete</v-icon>
                    <v-icon v-else>mdi-minus</v-icon>
                </v-btn>
            </template>
        </template>

        <template #[`item.showsDescendants`]="{ item, value }">
            <v-btn
                v-bind="{
                    ...attrs_btn__icon,
                    loading,
                    disabled: !!tempIdOnEdit,
                }"
                :color="value ? 'primary' : undefined"
                @click="save({ ...item, showsDescendants: !value })"
            >
                <v-icon v-if="value">mdi-eye</v-icon>
                <v-icon v-else>mdi-eye-off</v-icon>
            </v-btn>
        </template>

        <template #[`item.data-table-expand`]="{ expand, isExpanded }">
            <v-btn
                v-bind="{
                    ...attrs_btn__icon,
                    loading,
                    disabled: !!tempIdOnEdit,
                }"
                @click="expand(!isExpanded)"
            >
                <v-icon>
                    {{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
            </v-btn>
        </template>

        <template #expanded-item="{ headers, item }">
            <td :colspan="headers.length" style="padding: 0 0 0 43px">
                <v-row no-gutters>
                    <v-col cols="auto">
                        <v-divider vertical />
                    </v-col>
                    <v-col>
                        <user-code-table
                            v-model="item.children"
                            v-bind="{
                                parent: item,
                                loading,
                                tempIdOnEdit,
                            }"
                            class="rounded-0 transparent"
                            v-on="{
                                save,
                                pull,
                                setTempIdOnEdit: (tempId) => edit({ tempId }),
                            }"
                        />
                    </v-col>
                </v-row>
            </td>
        </template>

        <template #footer>
            <v-divider />
            <v-row no-gutters>
                <v-spacer />
                <v-col cols="auto">
                    <v-divider vertical />
                </v-col>
                <v-col cols="auto">
                    <v-btn
                        v-bind="{
                            ...attrs_btn__icon,
                            width: 44,
                            loading,
                            disabled: !!tempIdOnEdit,
                        }"
                        color="primary"
                        @click="push"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </v-data-table>
</template>

<script>
import { initUserCode, USER_CODE_TYPES } from "@/store/user-codes";
import { initDataTableHeaders } from "@/assets/lib";
import { attrs_btn__icon, attrs_input__table } from "@/assets/variables";

import AutocompleteMember from "../dumb/autocompletes/autocomplete-member.vue";
import dayjs from "dayjs";

/** @typedef {import('@/store/user-codes').UserCode} $UserCode*/

const items_type = Object.values(USER_CODE_TYPES);

const headers = initDataTableHeaders([
    {
        text: "#",
        value: "index",
        width: +44,
        align: "center",
        class: "px-0 py-2",
        cellClass: "px-0",
        formatter: (value, item) => (!!item?._id ? value : "new"),
    },
    {
        text: "회원",
        value: "_member",
        width: "50%",
        class: "px-3 py-2",
        cellClass: "pa-0",
    },
    {
        text: "추천인코드",
        value: "code",
        width: "50%",
        class: "px-3 py-2",
        cellClass: "pa-0",
    },
    {
        text: "사용\n시스템",
        value: "type",
        width: 140,
        class: "px-3 py-2",
        cellClass: "pa-0",
    },
    {
        text: "유보금\n비율",
        value: "reserveRate",
        width: 100,
        align: "end",
        class: "px-3 py-2",
        cellClass: "pa-0",
    },
    {
        text: "유보금\n매출\n(전일)",
        value: "saleSummary",
        width: 100,
        align: "end",
        class: "px-3 py-2",
        formatter: (value) => {
            let key = dayjs.tz().subtract(9, "day");
            key = key.format("YYMMDD");
            return (value[key] ?? 0).format() + "원";
        },
    },
    {
        text: "선물하기\n입금수수료\n(판매가)",
        value: "chargePrice",
        width: 100,
        align: "end",
        class: "px-3 py-2 charge-price-cell",
        cellClass: "pa-0 charge-price-cell",
    },
    {
        text: "선물하기\n입금수수료\n(원가)",
        value: "chargePrice_attrs.min",
        width: 100,
        align: "end",
        class: "px-3 py-2 charge-price-cell",
        cellClass: "px-0 charge-price-cell",
        formatterType: "number",
        formatterSuffix: " 원",
    },
    {
        text: "선물하기\n입금수수료\n(마진)",
        value: "chargePrice_margin",
        width: 100,
        align: "end",
        class: "px-3 py-2 charge-price-cell",
        cellClass: "px-3 charge-price-cell",
        formatter: (_, item) => `${(item.chargePrice - (item.chargePrice_cost || item.chargePrice_attrs.min)).format()} 원`,
    },
    {
        text: "모두의애드\n전환수수료\n(판매가)",
        value: "salePercent",
        width: 100,
        align: "end",
        class: "px-3 py-2 sale-percent-cell",
        cellClass: "pa-0 sale-percent-cell",
    },
    {
        text: "모두의애드\n전환수수료\n(원가)",
        value: "salePercent_attrs.min",
        width: 100,
        align: "end",
        class: "px-3 py-2 sale-percent-cell",
        cellClass: "px-0 sale-percent-cell",
        formatterType: "number",
        formatterSuffix: " %",
        formatterDigits: 2,
    },
    {
        text: "모두의애드\n전환수수료\n(마진)",
        value: "salePercent_margin",
        width: 100,
        align: "end",
        class: "px-3 py-2 sale-percent-cell",
        cellClass: "px-3 sale-percent-cell",
        formatter: (_, item) => `${(item.salePercent - (item.salePercent_cost || item.salePercent_attrs.min)).format(2)} %`,
    },
    {
        text: "수정\n저장",
        value: "save",
        width: +44,
        align: "center",
        class: "pa-0",
        cellClass: "pa-0",
    },
    {
        text: "삭제\n취소",
        value: "pull",
        width: +44,
        align: "center",
        class: "pa-0",
        cellClass: "pa-0",
    },
    {
        text: "하위\n통계",
        value: "showsDescendants",
        width: +44,
        align: "center",
        class: "pa-0",
        cellClass: "pa-0",
    },
    {
        text: "하위\n열람",
        value: "data-table-expand",
        width: +44,
        align: "center",
        class: "pa-0",
        cellClass: "pa-0",
    },
]);

export default {
    components: {
        AutocompleteMember,
    },
    name: "user-code-table",
    props: {
        value: { type: Array, default: () => [] },
        parent: { type: Object, default: initUserCode },
        loading: { type: Boolean, default: false },
        tempIdOnEdit: { type: String, default: null },
    },
    data: () => ({
        items: [],
        headers,

        items_type,

        attrs_btn__icon,
        attrs_input__table,
    }),
    methods: {
        sync() {
            this.items = [...this.value].map(initUserCode);
        },

        push() {
            let item = initUserCode({
                _parentCode: this.parent._id,

                depth: this.parent.depth + 1,

                chargePrice: this.parent.chargePrice,
                chargePrice_attrs: {
                    min: this.parent.chargePrice,
                },

                salePercent: this.parent.salePercent,
                salePercent_attrs: {
                    min: this.parent.salePercent,
                },
            });

            this.items.push(item);

            this.edit(item);
        },

        /** @param {$UserCode} item */
        pull(item) {
            if (!item._id) {
                let index = this.items.findIndex(({ tempId }) => tempId == item.tempId);
                if (-1 < index) this.items.splice(index, 1);

                return;
            }

            this.$emit("pull", item);
            this.edit();
        },

        /** @param {$UserCode} item */
        edit(item) {
            this.$emit("setTempIdOnEdit", item?.tempId || null);
        },

        /** @param {$UserCode} item */
        save(item) {
            this.$emit("save", item);
        },

        /**
         * @param {PointerEvent} event
         * @param {$UserCode} item
         */
        onClickField(event, item) {
            console.log({ event, item });
            if (!!this.tempIdOnEdit) return;

            this.edit(item);
            this.$nextTick(() => event?.target?.focus?.());
        },
    },
    watch: {
        value() {
            this.sync();
        },
    },
    mounted() {
        this.sync();
    },
};
</script>

<style lang="scss" scoped>
::v-deep th {
    line-height: 1.1;
}

::v-deep td,
::v-deep th {
    &.charge-price-cell {
        background-color: rgba(255, 255, 0, 0.075);
    }
    &.sale-percent-cell {
        background-color: rgba(0, 255, 255, 0.075);
    }
}

::v-deep .v-data-table__expanded__content {
    background-color: rgba(0, 0, 0, 0.02) !important;
}

.v-input.text-right {
    & ::v-deep input {
        text-align: end;
    }
}

.v-input.transparent {
    & ::v-deep .v-input__control,
    & ::v-deep .v-input__slot {
        background: transparent !important;
    }
}
</style>
